import { Component, HostBinding, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { ClientService } from '@core/services/client/client.service';
import { GlobalService } from '@core/services/common/global.service';
import { TranslationsService } from '@core/services/common/translations.service';
import { EditorService } from '@modules/editor/services/editor.service';
import { AuthService } from '@app/auth/services/auth.service';
import { LocalStorageService } from '@shared/services/storage';
import { environment } from '@env/environment';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy, OnInit {
  toggleControl = new FormControl(false);
  @HostBinding('attr.data-theme') theme = 'dark';
  title = '';
  private readonly resizeListener: () => void;
  private readonly scrollListener: () => void;
  private clientLocales = [];
  private clientDefaultLocale: string | null = null;
  private urlLanguage: string | null;
  private isEmptyUrl = false;
  pageData!: any;

  /** Odpięcie nasłuchu powrotu do karty — zwracane przez `Renderer2.listen`. */
  private visibilityListener?: () => void;

  constructor(
    private router: Router,
    private translation: TranslationsService,
    public clientService: ClientService,
    private globalService: GlobalService,
    private renderer: Renderer2,
    private editService: EditorService,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    route: ActivatedRoute,
  ) {
    this.globalService.pageWidth = window.innerWidth;
    this.resizeListener = this.renderer.listen('window', 'resize', (event) => {
      this.globalService.pageWidth = event.target.innerWidth;
    });
    this.scrollListener = this.renderer.listen('window', 'scroll', (event) => {
      this.globalService.scrollTop = event.target.scrollingElement.scrollTop;
    });

    clientService.availableLocales.subscribe((val) => {
      this.clientLocales = val;
      this.redirectToProperLocale();
    });
    clientService.clientDefaultLocale.subscribe((val) => {
      this.clientDefaultLocale = val;
      this.redirectToProperLocale();
    });


    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(({urlAfterRedirects}: any) => {
        this.addUrlToLocalStorage(urlAfterRedirects);
        this.clientService.navigatorHistory.push(urlAfterRedirects);
        const urlParts = urlAfterRedirects
          .split('/')
          .filter((part: string | any[]) => part.length);
        this.urlLanguage = urlParts.length ? urlParts[0] : undefined;
        const isKnownLocale =
          this.urlLanguage &&
          (this.clientLocales.includes(this.urlLanguage) ||
            environment.availableLocales.includes(this.urlLanguage));
        if (isKnownLocale) {
          this.translation.use(this.urlLanguage);
        }
        if (!urlParts.length) {
          this.isEmptyUrl = true;
          this.redirectToProperLocale();
        } else {
          this.isEmptyUrl = false;
        }
        globalService.scrollTop = 0;
        this.detectAndSaveAdminToken(route.snapshot.queryParams);
      });
  }

  private detectAndSaveAdminToken(params: Params) {
    const authToken = params['authToken'];
    const redirectUrl = params['redirectUrl'];
    const newParams: Params = {
      ...params,
      authToken: undefined,
      redirectUrl: undefined,
    };

    if (redirectUrl) {
      this.localStorageService.setItem('redirectUrl', redirectUrl);
    }

    if (authToken) {
      this.localStorageService.setItem('adminAuthToken', authToken);
      this.router.navigate([window.location.pathname], {
        queryParams: newParams,
        fragment: 'edit',
      });
    }
  }

  ngOnInit(): void {
    this.toggleControl.valueChanges.subscribe((darkMode) => {
      document.documentElement.setAttribute(
        'data-theme',
        darkMode ? 'dark' : 'light',
      );
    });
    // Automatycznie przetwórz istniejące elementy
    document.querySelectorAll('.editeable').forEach((el) => {
      this.editService['setupEditeableElement'](el as HTMLElement);
    });

    // AT-9483, przypadek szósty. Sklep nie przedłużał sesji klienta ANI RAZU: odnowienie było
    // wyłącznie reakcją na odmowę 401, a trasa odnawiania stoi za strażą uwierzytelnienia —
    // żądanie z wygasłym tokenem w ogóle do niej nie dociera. Sesja kończyła się więc zawsze
    // po swoim pierwotnym terminie, choćby klient wracał do sklepu codziennie.
    //
    // Start aplikacji i powrót do karty: oba są tanie, bo sam warunek to dwa odczyty
    // z magazynu, a żądanie leci dopiero po przekroczeniu połowy okresu ważności.
    this.authService.renewIfHalfWindowPassed();
    this.visibilityListener = this.renderer.listen('document', 'visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.authService.renewIfHalfWindowPassed();
      }
    });
  }

  ngOnDestroy() {
    this.resizeListener();
    this.scrollListener();
    this.visibilityListener?.();
  }

  private addUrlToLocalStorage(url: string): void {
    const history = this.localStorageService.getItem('history') || '[]';
    const historyArr = JSON.parse(history);
    if (!historyArr.length || historyArr[historyArr.length - 1] !== url) {
      historyArr.push(url);
    }
    if (historyArr.length > 20) {
      historyArr.length = 20;
    }
    this.localStorageService.setItem('history', JSON.stringify(historyArr));
  }

  private redirectToProperLocale() {
    if (
      !this.clientDefaultLocale ||
      !this.clientLocales.length ||
      !this.isEmptyUrl
    ) {
      return;
    }
  }
}
